import { useState, useRef, useEffect } from "react";
import { uploadVideoChunk } from "../../../services/test";
import { ReactSession } from "../../../lib/secure_reactsession";
import { useNavigate } from "react-router-dom";

export default function TabRecorder({ start, test, result, end }) {
    const [recording, setRecording] = useState(false);
    const [mediaStream, setMediaStream] = useState(null);
    const [manualStop, setManualStop] = useState(false); // Track if stopped by browser button
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const accumulatedSizeRef = useRef(0);
    const chunkSizeThreshold = 512 * 1024; // 512 KB
    let chunksCount = useRef(0); // To keep track of the number of chunks sent

    const navigate = useNavigate();
    const permissionDenied = () => {
        !ReactSession.get("token")
            ? navigate("/tester/sign-in")
            : navigate("/");
    };

    // Start recording when `start` prop is true
    useEffect(() => {
        if (start && !recording && !manualStop) {
            startRecording();
        }
    }, [start, recording, manualStop]);

    // Stop recording automatically when `end` prop becomes true
    useEffect(() => {
        if (end && recording) {
            console.log("closed by passing end true");
            stopRecording(false); // Pass false to indicate it's not manual stop
        }
    }, [end, recording]);

    // Detect when user manually stops sharing via browser UI
    useEffect(() => {
        if (mediaStream) {
            const videoTrack = mediaStream.getVideoTracks()[0];
            videoTrack.addEventListener("ended", () => {
                setManualStop(true);
                stopRecording(true); // Pass true to indicate it's a manual stop
                navigate('/t/test-ended');
            });
        }
    }, [mediaStream]);

    useEffect(() => {
        if (mediaStream) {
            console.log("Media stream available, delaying recording by 0.5s...");

            // Add a 0.5-second delay before starting the recording
            const timer = setTimeout(() => {
                const mediaRecorder = new MediaRecorder(mediaStream, {
                    mimeType: "video/webm; codecs=vp9,opus",
                });

                mediaRecorderRef.current = mediaRecorder;
                recordedChunksRef.current = [];
                accumulatedSizeRef.current = 0;

                mediaRecorder.ondataavailable = handleDataAvailable;
                mediaRecorder.onstop = handleRecordingStop;

                mediaRecorder.start(1000); // Start collecting data every second
                setRecording(true);

                const videoTrack = mediaStream.getVideoTracks()[0];
                videoTrack.addEventListener("ended", () => {
                    console.log("User manually stopped recording.");
                    setManualStop(true);
                    stopRecording(true);
                    navigate('/t/test-ended');
                });
            }, 500); // 500ms delay

            return () => clearTimeout(timer); // Cleanup timeout if `mediaStream` changes
        }
    }, [mediaStream]);

    const startRecording = async () => {
        try {
            console.log("Requesting screen recording permission...");
            const stream = await navigator.mediaDevices.getDisplayMedia({
                preferCurrentTab: true,
                video: {
                    displaySurface: "browser",
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 10, max: 30 },
                    aspectRatio: 16 / 9,
                    cursor: "always"
                },
                audio: false,
            });

            console.log("Stream obtained:", stream);

            const videoTrack = stream.getVideoTracks()[0];
            const settings = videoTrack.getSettings();

            if (settings.displaySurface !== "browser") {
                alert("Please select the current tab for recording.");
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            if (stream) {
                setMediaStream(stream);
            } else {
                console.error("Failed to get a valid stream.");
            }
        } catch (error) {
            console.error("Error starting tab recording:", error);
            permissionDenied();
        }
    };

    const stopRecording = (isManualStop) => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.requestData(); 
            mediaRecorderRef.current.stop(); // Stop the recording
            console.log("MediaRecorder stopped.");
        }

        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
            console.log("Screen capture stopped.");
        }

        setRecording(false);

        if (recordedChunksRef.current.length > 0) {
            const status = isManualStop ? "progress" : "ended";
            sendChunkToServer(
                new Blob(recordedChunksRef.current, { type: "video/webm" }),
                status
            );
            recordedChunksRef.current = [];
            accumulatedSizeRef.current = 0;
        }
    };

    const handleDataAvailable = (event) => {
        if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
            accumulatedSizeRef.current += event.data.size;

            if (accumulatedSizeRef.current >= chunkSizeThreshold) {
                sendChunkToServer(
                    new Blob(recordedChunksRef.current, { type: "video/webm" }),
                    "progress"
                );
                recordedChunksRef.current = [];
                accumulatedSizeRef.current = 0;
            }
        }
    };

    const sendChunkToServer = async (blob, status = "progress") => {

        var last_chunk = false;

        if(status === "ended") {
            last_chunk = true;
        }
        if (chunksCount.current === 0) {
            status = "started";
        }

        const formData = new FormData();
        formData.append("test_result_id", result.id);
        formData.append("test_id", test.id);
        formData.append("video_record", blob, `chunk-${Date.now()}.webm`);
        formData.append("recording", status);
        formData.append("last_chunk", last_chunk);
        formData.append("guest_token", ReactSession.get("guest_token"));

        try {
            const token = ReactSession.get("token");
            const data = await uploadVideoChunk(formData, token);
            chunksCount.current++;
            console.log("Chunk uploaded successfully", data, chunksCount.current);
        } catch (error) {
            console.error("Error uploading chunk", error);
        }
    };

    const handleRecordingStop = () => {
        console.log("Recording stopped.");
    };

    return null;
}
